import { useState, useEffect, useCallback, useRef } from "react";

// Declare build time constant injected by Vite
declare const __BUILD_TIME__: string;

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const DISMISS_DURATION = 30 * 60 * 1000; // Dismiss for 30 minutes
const VERSION_TOLERANCE_MS = 60 * 1000; // 1 minute tolerance for version comparison
const POST_UPDATE_DELAY_MS = 10 * 1000; // Wait 10 seconds after update before checking

interface VersionCheckState {
  isOutdated: boolean;
  serverVersion: string | null;
  localVersion: string;
  isChecking: boolean;
  isDismissed: boolean;
}

export function useVersionCheck() {
  const [state, setState] = useState<VersionCheckState>({
    isOutdated: false,
    serverVersion: null,
    localVersion: __BUILD_TIME__ || "dev",
    isChecking: false,
    isDismissed: false,
  });
  
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasCheckedAfterUpdate = useRef(false);

  const checkVersion = useCallback(async () => {
    // Don't check if offline
    if (!navigator.onLine) {
      console.log("[VERSION-CHECK] Offline, skipping check");
      return;
    }

    setState(prev => ({ ...prev, isChecking: true }));

    try {
      // Fetch version.json with cache busting
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
        },
      });

      if (!response.ok) {
        console.log("[VERSION-CHECK] version.json not found (likely dev mode)");
        setState(prev => ({ ...prev, isChecking: false }));
        return;
      }

      const data = await response.json();
      const serverBuildTime = data.buildTime;

      if (!serverBuildTime) {
        console.log("[VERSION-CHECK] No buildTime in version.json");
        setState(prev => ({ ...prev, isChecking: false }));
        return;
      }

      const localTime = new Date(__BUILD_TIME__).getTime();
      const serverTime = new Date(serverBuildTime).getTime();

      // Server version is newer if timestamp difference is greater than tolerance
      // This prevents false positives from minor timing differences
      const timeDifference = serverTime - localTime;
      const isOutdated = timeDifference > VERSION_TOLERANCE_MS;

      console.log("[VERSION-CHECK] Local:", __BUILD_TIME__, "Server:", serverBuildTime, "Diff:", timeDifference, "ms, Outdated:", isOutdated);

      setState(prev => ({
        ...prev,
        isOutdated,
        serverVersion: serverBuildTime,
        isChecking: false,
      }));
    } catch (error) {
      console.error("[VERSION-CHECK] Error checking version:", error);
      setState(prev => ({ ...prev, isChecking: false }));
    }
  }, []);

  const dismiss = useCallback(() => {
    setState(prev => ({ ...prev, isDismissed: true }));

    // Clear any existing timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
    }

    // Reset dismissed state after duration
    dismissTimeoutRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, isDismissed: false }));
    }, DISMISS_DURATION);
  }, []);

  // Initial check and periodic checks
  useEffect(() => {
    // Check if we just came from an update
    const updateTimestamp = localStorage.getItem("pwa_updating");
    
    const performInitialCheck = () => {
      // Clear the update flag after checking
      if (updateTimestamp) {
        localStorage.removeItem("pwa_updating");
        hasCheckedAfterUpdate.current = true;
        console.log("[VERSION-CHECK] Post-update check completed, flag cleared");
      }
      checkVersion();
    };
    
    if (updateTimestamp) {
      // We just updated - delay the check to let Service Worker settle
      const updateTime = parseInt(updateTimestamp, 10);
      const timeSinceUpdate = Date.now() - updateTime;
      
      if (timeSinceUpdate < POST_UPDATE_DELAY_MS) {
        const remainingDelay = POST_UPDATE_DELAY_MS - timeSinceUpdate;
        console.log("[VERSION-CHECK] Post-update delay:", remainingDelay, "ms");
        setTimeout(performInitialCheck, remainingDelay);
      } else {
        // Enough time has passed, check now
        performInitialCheck();
      }
    } else {
      // Normal startup - check immediately
      checkVersion();
    }

    // Setup periodic check
    checkIntervalRef.current = setInterval(checkVersion, CHECK_INTERVAL);

    // Listen for online event to check when connection returns
    const handleOnline = () => {
      console.log("[VERSION-CHECK] Back online, checking version");
      checkVersion();
    };
    window.addEventListener("online", handleOnline);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
      window.removeEventListener("online", handleOnline);
    };
  }, [checkVersion]);

  return {
    ...state,
    checkNow: checkVersion,
    dismiss,
  };
}
